import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSetup, useUpdateSetup } from '../hooks/useSetup.ts';
import { loadOnboardingStep, saveOnboardingStep } from '../lib/setup.ts';
import { ConnectStep, DevicesStep, DoneStep, HistoryStep } from '../components/setup/WizardSteps.tsx';

const STEPS = ['devices', 'connect', 'history', 'done'] as const;
const LAST = STEPS.length - 1;

/**
 * Onboarding for new accounts (docs/ONBOARDING_PLAN.md §3, web): Devices →
 * Connect → Older rides → Done. Each step can be skipped; the current step
 * is remembered so an interrupted wizard resumes where it stopped. Anyone
 * who doesn't need onboarding (or once it's completed) goes to Dashboard.
 */
export function Welcome() {
  const { t } = useTranslation();
  const [step, setStep] = useState(() => loadOnboardingStep(LAST));
  const { data, isError } = useSetup({ poll: STEPS[step] === 'connect' });
  const finish = useUpdateSetup();

  useEffect(() => saveOnboardingStep(step), [step]);

  if (isError || data?.onboarding_required === false) return <Navigate to="/rides" replace />;
  if (!data) return null;

  const next = () => setStep((s) => Math.min(s + 1, LAST));
  const onFinish = () =>
    finish.mutate({ completed: true }, { onSuccess: () => saveOnboardingStep(null) });

  return (
    <div className="min-h-screen overflow-y-auto bg-(--color-bg-primary)">
      <div className="max-w-xl mx-auto px-4 py-10 flex flex-col gap-5">
        <ProgressDots step={step} label={t('setup.wizard.progress', { step: step + 1, total: STEPS.length })} />
        {STEPS[step] === 'devices' && <DevicesStep setup={data} onNext={next} />}
        {STEPS[step] === 'connect' && <ConnectStep setup={data} onNext={next} />}
        {STEPS[step] === 'history' && <HistoryStep setup={data} onNext={next} />}
        {STEPS[step] === 'done' && <DoneStep setup={data} onFinish={onFinish} finishing={finish.isPending} />}
      </div>
    </div>
  );
}

function ProgressDots({ step, label }: Readonly<{ step: number; label: string }>) {
  return (
    <div className="flex items-center gap-2">
      <p className="sr-only">{label}</p>
      {STEPS.map((name, i) => (
        <span
          key={name}
          aria-hidden="true"
          className={`h-2 rounded-full transition-all ${dotClass(i, step)}`}
        />
      ))}
    </div>
  );
}

function dotClass(i: number, step: number): string {
  if (i === step) return 'w-6 bg-(--color-accent)';
  return i < step ? 'w-2 bg-(--color-accent)/50' : 'w-2 bg-(--color-border)';
}
